import type { GettingStarted } from '../profiles/api';
import type { ApiLetter, MyStamps, StampBookView, StampDesign } from '../letters/api';
import { usableCollections } from '../postage/designs';
import type { RelationshipWithPeer } from '../relationships/api';
import { ITEMS, type Item } from '../onboarding/GettingStarted';

/**
 * What the home panel shows, worked out from data the app already has. Pure,
 * so the tests can pin it down. Anything missing hides its tile rather than
 * inventing something to say.
 */

/** The bit of a conversation summary the panel reads (Messenger's list rows). */
export interface PanelConversation {
  relationshipId: string;
  hidden: boolean;
  peer: RelationshipWithPeer['peer'];
  last: ApiLetter | null;
  lastMine: boolean;
  unreadCount: number;
  peerWriting: boolean;
  /** A letter-in-progress for this person, saved on this device. */
  draft?: { body: string; updatedAt: number } | null;
}

export interface ProfileProgress {
  done: Item[];
  undone: Item[];
  total: number;
  next: Item | null;
}

/** Getting started as progress. Null once it's put away or there's nothing left. */
export function profileProgress(gs: GettingStarted | null | undefined): ProfileProgress | null {
  if (!gs) return null;
  const keys = ITEMS.map((i) => i.key);
  const done = keys.filter((k) => gs[k]);
  const undone = keys.filter((k) => !gs[k]);
  if (undone.length === 0) return null;
  return { done, undone, total: keys.length, next: undone[0] ?? null };
}

export type PostItem =
  | { kind: 'coming'; relationshipId: string; peer: NonNullable<RelationshipWithPeer['peer']>; deliverAt: string }
  | { kind: 'writing'; relationshipId: string; peer: NonNullable<RelationshipWithPeer['peer']>; landedAt: string | null };

/**
 * In the post: letters on their way to me (soonest first), then the people
 * writing back to me (the pen). Pairs kept hidden are never named here.
 */
export function inThePost(conversations: PanelConversation[]): PostItem[] {
  const coming: PostItem[] = [];
  const writing: PostItem[] = [];
  for (const c of conversations) {
    if (c.hidden || !c.peer || !c.last) continue;
    const l = c.last;
    if (!c.lastMine && (l.state === 'sealed' || l.state === 'in_transit') && l.deliverAt && l.kind !== 'overture') {
      coming.push({ kind: 'coming', relationshipId: c.relationshipId, peer: c.peer, deliverAt: l.deliverAt });
    } else if (c.lastMine && c.peerWriting) {
      writing.push({ kind: 'writing', relationshipId: c.relationshipId, peer: c.peer, landedAt: l.deliveredAt });
    }
  }
  coming.sort((a, b) => (a.kind === 'coming' && b.kind === 'coming' ? a.deliverAt.localeCompare(b.deliverAt) : 0));
  return [...coming, ...writing];
}

/** Letters that have arrived and wait to be opened, one row per person. */
export function waitingToOpen(conversations: PanelConversation[]): PanelConversation[] {
  return conversations.filter((c) => !c.hidden && c.peer && c.unreadCount > 0);
}

/* ── Today's tasks: whose turn it is, and what is half done ─────────────── */

/**
 * Something waiting on me. Only ever my turn: a letter on its way to me asks
 * nothing yet, and a letter of mine that landed is theirs to answer.
 */
export type TodayTask =
  | { kind: 'open'; id: string; relationshipId: string; peerName: string; count: number }
  | { kind: 'reply'; id: string; relationshipId: string; letterId: string; peerName: string; deliveredAt: string | null }
  | { kind: 'finish-reply'; id: string; relationshipId: string; letterId: string; peerName: string }
  | { kind: 'finish-letter'; id: string; relationshipId: string; peerName: string }
  | { kind: 'push'; id: 'push' };

function nameOf(peer: NonNullable<RelationshipWithPeer['peer']>): string {
  return peer.handle ? `@${peer.handle}` : (peer.displayName ?? 'someone');
}

/**
 * The to-do list, most pressing first: letters waiting to be opened, replies
 * already started, letters to answer (longest waiting first), letters started
 * to someone, then turning on notifications for this device. Pairs kept
 * hidden, the Post Office and overtures are never listed — none can be named
 * or answered here.
 */
export function todayTasks(
  conversations: PanelConversation[],
  { offerPush = false, postOfficeHandle = 'postmaster' }: { offerPush?: boolean; postOfficeHandle?: string } = {},
): TodayTask[] {
  const open: TodayTask[] = [];
  const finishReply: TodayTask[] = [];
  const reply: Extract<TodayTask, { kind: 'reply' }>[] = [];
  const finishLetter: TodayTask[] = [];
  for (const c of conversations) {
    if (c.hidden || !c.peer) continue;
    const peerName = nameOf(c.peer);
    const l = c.last;
    const drafted = Boolean(c.draft?.body.trim());
    if (c.unreadCount > 0) {
      open.push({ kind: 'open', id: `open-${c.relationshipId}`, relationshipId: c.relationshipId, peerName, count: c.unreadCount });
      continue;
    }
    if (c.peer.handle === postOfficeHandle) continue;
    // Their letter is the last word and it has landed: my turn.
    if (l && !c.lastMine && l.kind === 'letter' && (l.state === 'delivered' || l.state === 'opened')) {
      if (drafted) {
        finishReply.push({ kind: 'finish-reply', id: `finish-${c.relationshipId}`, relationshipId: c.relationshipId, letterId: l.id, peerName });
      } else {
        reply.push({ kind: 'reply', id: `reply-${c.relationshipId}`, relationshipId: c.relationshipId, letterId: l.id, peerName, deliveredAt: l.deliveredAt });
      }
      continue;
    }
    // A letter started where one can be sent: a first letter, or after a settled exchange.
    const writable = !l || l.state === 'answered' || l.state === 'cancelled';
    if (drafted && writable && l?.kind !== 'overture') {
      finishLetter.push({ kind: 'finish-letter', id: `finish-${c.relationshipId}`, relationshipId: c.relationshipId, peerName });
    }
  }
  reply.sort((a, b) => (a.deliveredAt ?? '').localeCompare(b.deliveredAt ?? ''));
  const tasks = [...open, ...finishReply, ...reply, ...finishLetter];
  if (offerPush) tasks.push({ kind: 'push', id: 'push' });
  return tasks;
}

export interface StampSummary {
  /** The stamp counter is open: Express stamps and 1st class passes count. */
  counter: boolean;
  express: number;
  firstPasses: number;
  nextAllowanceOn: string | null;
  /** Custom stamp designs this member can print with, the one they use first. */
  designs: StampDesign[];
}

const MINI_STAMPS = 3;

/**
 * The stamp book at a glance: the postage it holds (from the stamp book) and
 * the custom designs it can print them with (from /me/stamps — the same books
 * the stamp book page shows). Null when it has neither.
 */
export function stampSummary(
  book: StampBookView | undefined,
  stamps?: MyStamps,
  chosenDesignId: string | null = null,
): StampSummary | null {
  const all = usableCollections(stamps).flatMap((c) => c.designs);
  const chosen = all.find((d) => d.id === chosenDesignId);
  const designs = (chosen ? [chosen, ...all.filter((d) => d !== chosen)] : all).slice(0, MINI_STAMPS);
  const counter = Boolean(book?.enabled);
  if (!counter && designs.length === 0) return null;
  const express = counter ? book!.pages.reduce((n, p) => n + p.express, 0) + book!.anyLetter.express : 0;
  const firstPasses = counter ? book!.pages.filter((p) => p.firstClass).length + book!.anyLetter.first : 0;
  return { counter, express, firstPasses, nextAllowanceOn: counter ? book!.nextAllowanceOn : null, designs };
}

export interface FeatureCard {
  id: 'stationery' | 'stamp-designs';
  title: string;
  body: string;
  cta: string;
  plus: boolean;
}

/**
 * New in toiyp: only what this member's account actually offers (the books
 * the server lists), newest feature first. Plus marks a Plus-only book.
 */
export function whatsNew(stamps: MyStamps | undefined): FeatureCard[] {
  if (!stamps) return [];
  const cards: FeatureCard[] = [];
  const withPaper = stamps.collections.filter((c) => c.papers.length + c.envelopes.length > 0);
  if (withPaper.length > 0) {
    cards.push({
      id: 'stationery',
      title: withPaper.some((c) => c.envelopes.length > 0) ? 'Letters now come in envelopes' : 'Letter paper to write on',
      body: 'Pick your paper, seal it up, and it stays sealed until it lands.',
      cta: 'See the paper',
      plus: withPaper.every((c) => c.plusOnly),
    });
  }
  const withDesigns = stamps.collections.filter((c) => c.designs.length > 0);
  if (withDesigns.length > 0) {
    cards.push({
      id: 'stamp-designs',
      title: 'Stamps with pictures on',
      body: 'Print your stamps with a design. The class mark stays in the corner.',
      cta: 'See the stamps',
      plus: withDesigns.every((c) => c.plusOnly),
    });
  }
  return cards;
}

/** "Thursday evening". */
export function dayPart(now: Date): string {
  const h = now.getHours();
  const part = h < 5 ? 'night' : h < 12 ? 'morning' : h < 17 ? 'afternoon' : h < 22 ? 'evening' : 'night';
  return `${now.toLocaleDateString([], { weekday: 'long' })} ${part}`;
}

/** The name to greet: the first word of the display name, else the handle. */
export function greetingName(displayName: string | null | undefined, handle: string | null | undefined): string | null {
  const first = displayName?.trim().split(/\s+/)[0];
  return first || handle || null;
}

/** Calendar days since, in local time: "today", "yesterday", "3 days ago". */
export function daysAgo(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  const midnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((midnight(now) - midnight(then)) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

/** "Your letter landed 3 days ago." */
export function landedPhrase(iso: string, now: Date = new Date()): string {
  return `Your letter landed ${daysAgo(iso, now)}.`;
}

/* ── Short phones: one nudge in the letters list ─────────────────────────── */

export type NudgeItem =
  | { kind: 'task'; id: string; task: TodayTask }
  | { kind: 'delivered'; id: string; round: DeliveredRound }
  | { kind: 'step'; id: string; step: Item }
  | { kind: 'share'; id: 'share' }
  | { kind: 'new'; id: string; feature: FeatureCard };

/**
 * Everything the nudge could say, most useful first: the letters waiting on
 * me (to open, answer or finish), "who else deserves a letter?" just after one
 * of mine landed, turning on notifications here, the next Getting started
 * step, sharing your toiyp, what's new.
 */
export function nudgeCandidates(
  conversations: PanelConversation[],
  progress: ProfileProgress | null,
  features: FeatureCard[],
  delivered: DeliveredRound | null = null,
  offerPush = false,
): NudgeItem[] {
  const tasks = todayTasks(conversations, { offerPush });
  const items: NudgeItem[] = tasks.filter((t) => t.kind !== 'push').map((t) => ({ kind: 'task', id: t.id, task: t }));
  if (delivered) items.push({ kind: 'delivered', id: `delivered-${delivered.letterId}`, round: delivered });
  const push = tasks.find((t) => t.kind === 'push');
  if (push) items.push({ kind: 'task', id: push.id, task: push });
  if (progress?.next) items.push({ kind: 'step', id: `step-${progress.next}`, step: progress.next });
  items.push({ kind: 'share', id: 'share' });
  const feature = features[0];
  if (feature) items.push({ kind: 'new', id: `new-${feature.id}`, feature });
  return items;
}

/** What "Not now" remembers: the app day it was pressed, and the item it put away. */
export interface NudgeDismissal {
  day: string;
  id: string;
}

/** The local calendar day, `YYYY-MM-DD` — "until tomorrow" in the member's own time. */
export function appDay(now: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

/**
 * The one nudge to show, or null. Put away today → nothing until tomorrow;
 * then it moves past the item that was put away, unless that's all there is.
 */
export function pickNudge(
  candidates: NudgeItem[],
  dismissed: NudgeDismissal | null,
  now: Date,
): NudgeItem | null {
  if (dismissed && dismissed.day === appDay(now)) return null;
  const rest = dismissed ? candidates.filter((c) => c.id !== dismissed.id) : candidates;
  return rest[0] ?? candidates[0] ?? null;
}

/** "While you wait for @grace" when a letter is on its way to you, else "Your next step". */
export function nudgeContext(post: PostItem[]): string {
  const coming = post.find((p) => p.kind === 'coming');
  if (!coming) return 'Your next step';
  return `While you wait for ${coming.peer.handle ? `@${coming.peer.handle}` : (coming.peer.displayName ?? 'them')}`;
}

/* ── After a delivery: "Who else deserves a letter from you?" ─────────────── */

/** How long "Not now" puts the delivered prompt away, at least. */
export const DELIVERED_PROMPT_REST_DAYS = 7;

/** A delivery older than this is not news any more, so it asks nothing. */
export const DELIVERED_PROMPT_FRESH_DAYS = 7;

/** What the delivered prompt remembers on this device. */
export interface DeliveredPromptMemory {
  /** The letter whose delivery it was last shown for: once per delivered round. */
  letterId: string | null;
  /** "Not now": nothing again before this (ISO). */
  restUntil: string | null;
}

export interface DeliveredRound {
  letterId: string;
  relationshipId: string;
  deliveredAt: string;
  /** `@grace` — only ever a pair that is not hidden. */
  peerName: string;
}

/**
 * The newest letter of mine that has landed and not yet been answered, if it
 * landed recently. Pairs kept hidden, the Post Office and overtures never
 * count, so the prompt can never point at a hidden correspondent.
 */
export function deliveredRound(
  conversations: PanelConversation[],
  now: Date,
  postOfficeHandle = 'postmaster',
): DeliveredRound | null {
  let best: DeliveredRound | null = null;
  const fresh = now.getTime() - DELIVERED_PROMPT_FRESH_DAYS * 86_400_000;
  for (const c of conversations) {
    const l = c.last;
    if (c.hidden || !c.peer || !c.lastMine || !l || !l.deliveredAt) continue;
    if (c.peer.handle === postOfficeHandle || l.kind === 'overture') continue;
    if (l.state !== 'delivered' && l.state !== 'opened') continue;
    const at = new Date(l.deliveredAt).getTime();
    if (at < fresh || at > now.getTime()) continue;
    if (!best || l.deliveredAt > best.deliveredAt) {
      const peerName = c.peer.handle ? `@${c.peer.handle}` : (c.peer.displayName ?? 'them');
      best = { letterId: l.id, relationshipId: c.relationshipId, deliveredAt: l.deliveredAt, peerName };
    }
  }
  return best;
}

/**
 * Whether to ask, for this delivered round: never while "Not now" is resting,
 * and only once per round — `shownThisVisit` keeps it up for the rest of the
 * visit it first appeared in, and no later one.
 */
export function showDeliveredPrompt(
  round: DeliveredRound | null,
  memory: DeliveredPromptMemory,
  now: Date,
  shownThisVisit: string | null,
): boolean {
  if (!round) return false;
  if (memory.restUntil && new Date(memory.restUntil).getTime() > now.getTime()) return false;
  if (memory.letterId === round.letterId) return shownThisVisit === round.letterId;
  return true;
}

/** "Not now": rest for a week from now. */
export function restDeliveredPrompt(letterId: string, now: Date): DeliveredPromptMemory {
  return { letterId, restUntil: new Date(now.getTime() + DELIVERED_PROMPT_REST_DAYS * 86_400_000).toISOString() };
}
